import { createContext, useEffect, useState } from 'react'
import { useMutation } from 'react-query'
import { MemoryRouter, NavLink, Prompt, Redirect, Route, Switch, useHistory, useParams } from 'react-router-dom'

import Breadcrumbs from '@/components/TailwindUI/Breadcrumbs'
import Button from '@/components/TailwindUI/Button'
import ConfirmActionDialog from '@/components/TailwindUI/ConfirmDialog'
import Image from '@/components/TailwindUI/Image'
import { useNotification } from '@/hooks/useNotification'
import { getAccessToken, getSpaceId } from '@/store/contextSubscriber'
import cn from '@/utilities/cn'

import { Actions } from './Actions/Actions'
import { DatasourceCatalog } from './DatasourceCatalog'
import { Events } from './Events/Events'
import { Export } from './Export'
import { Metadata } from './Metadata'
import { Schema } from './Schema'
import { Test } from './Test'

type Datasource = {
  id: string
  iconUrl?: string
  name: string
  version: string
  schema: Record<string, any>
  actions: Record<string, any>
  events: Record<string, any>
  authTypes: string[]
}

export const DatasourceContext = createContext<ReturnType<typeof useState<Datasource>>>(null!)

export default function DataSourcesBuilderPage() {
  const [datasource, setDatasource] = useState<Datasource | undefined>(() => {
    const value = localStorage.getItem('datasource')
    return value ? JSON.parse(value) : undefined
  })
  const [showExitWarningModal, setShowExitWarningModal] = useState(false)
  const [showSaveWarningModal, setShowSaveWarningModal] = useState(false)

  const { spaceId } = useParams<{ spaceId: string }>()

  useEffect(() => {
    if (!datasource) return
    localStorage.setItem('datasource', JSON.stringify(datasource))
  }, [datasource])
  const history = useHistory()

  const { notify } = useNotification()

  const mutation = useMutation({
    mutationFn: async (ds: any) => {
      let response
      if (process.env.NODE_ENV === 'production') {
        response = await fetch(`/v3/appbuilder/spaces/${getSpaceId()}/datasources`, {
          method: 'put',
          headers: {
            Authorization: `Bearer ${getAccessToken()}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(ds)
        })
      } else {
        // modifing the url to account for when we are running the workgrid-datasource-api locally
        response = await fetch(`http://localhost:3001/api/v1/datasources`, {
          method: 'put',
          headers: {
            'Content-Type': 'application/json',
            // local dev headers to satisfy schema
            'x-workgrid-space': 'spaceId',
            'x-workgrid-tenant': 'tenantId',
            'x-workgrid-user': 'userId'
          },
          body: JSON.stringify(ds)
        })
      }
      if (response.ok) {
        return response.json()
      } else {
        throw new Error(response.statusText)
      }
    },
    onSuccess: () => {
      notify({
        variant: 'success',
        title: 'Datasource Changes Saved',
        body: (
          <div className="text-sm text-zinc-500">
            <span className="font-bold">{datasource?.name}</span> <span> has been successfully updated.</span>
          </div>
        )
      })
    },
    onError: () => {
      notify({
        variant: 'error',
        title: 'Failed to save datasource',
        body: (
          <div className="text-sm text-zinc-500">
            <span className="font-bold">{datasource?.name}</span> <span> could not be saved.</span>
          </div>
        )
      })
    }
  })

  return (
    <DatasourceContext.Provider value={[datasource, setDatasource]}>
      <Prompt
        message={params =>
          params.pathname.includes('/datasources/builder')
            ? true
            : 'Are you sure you want to abort editing this datasource?'
        }
      />
      <MemoryRouter>
        <ConfirmActionDialog
          isOpen={showExitWarningModal}
          onConfirm={() => {
            setShowExitWarningModal(false)
            setDatasource(undefined)
            history.replace(`/space/${spaceId}/appbuilder/datasources/builder`)
          }}
          onCancel={() => setShowExitWarningModal(false)}
          message={'Are you sure you want to abort editing this datasource? '}
          subMessage={'Please export the datasource if you wish to save changes.'}
          variant="info"
        />
        <ConfirmActionDialog
          isOpen={showSaveWarningModal}
          loading={mutation.isLoading}
          loadingText={'Saving...'}
          onConfirm={async () => {
            setShowSaveWarningModal(false)
            await mutation.mutateAsync(datasource)
          }}
          onCancel={() => setShowSaveWarningModal(false)}
          message={'Saved changes here will be replaced when the Data Source API repository is deployed.'}
          subMessage={
            'Please export the Data Source YAML into the Data Source  API repository if you do not want to lose your changes.'
          }
          variant="warning"
        />
        <div className="flex flex-1 flex-col">
          {datasource ? (
            <div className="flex flex-col gap-4 border-b border-gray-200 p-4 sm:px-6 lg:px-8">
              <Breadcrumbs
                className="flex py-2"
                breadcrumbs={[
                  {
                    label: 'Data Sources',
                    path: `/space/${spaceId}/appbuilder/datasources/builder`,
                    onClick: () => {
                      setShowExitWarningModal(true)
                    }
                  },
                  {
                    label: datasource ? datasource?.name : 'Loading...'
                  }
                ]}
              />

              <div className="flex items-center justify-between">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center">
                    <h2 className="text-2xl font-bold leading-7 tracking-tight text-gray-900">
                      {datasource.name} ({datasource.version})
                    </h2>

                    {datasource.iconUrl && (
                      <Image className={cn('ml-4 rounded-lg', 'h-8 w-8 flex-none')} src={datasource.iconUrl} />
                    )}
                  </div>
                </div>
              </div>

              <nav className="flex justify-between">
                <ul className="flex items-center gap-x-6 text-sm font-semibold leading-6">
                  <li>
                    <NavLink to="/metadata" className="text-gray-400" activeClassName="text-blue-600">
                      Metadata
                    </NavLink>
                  </li>
                  <li>
                    <NavLink to="/schema" className="text-gray-400" activeClassName="text-blue-600">
                      Schema
                    </NavLink>
                  </li>
                  <li>
                    <NavLink to="/actions" className="text-gray-400" activeClassName="text-blue-600">
                      Actions
                    </NavLink>
                  </li>
                  <li>
                    <NavLink to="/events" className="text-gray-400" activeClassName="text-blue-600">
                      Events
                    </NavLink>
                  </li>
                </ul>

                <ul className="flex items-center gap-x-6 text-sm font-semibold leading-6">
                  <li>
                    <NavLink to="/test" className="text-gray-400" activeClassName="text-blue-600">
                      Test
                    </NavLink>
                  </li>
                  <li>
                    <NavLink to="/export" className="text-gray-400" activeClassName="text-blue-600">
                      Import / Export
                    </NavLink>
                  </li>
                  {(window.location.origin.includes('.dev.workgrid.com') ||
                    window.location.origin.includes('localhost')) && (
                    <li>
                      <Button
                        onClick={() => {
                          setShowSaveWarningModal(true)
                        }}
                        color="primary"
                      >
                        Save
                      </Button>
                    </li>
                  )}
                </ul>
              </nav>
            </div>
          ) : null}

          <Switch>
            <Route path="/" exact render={() => <DatasourceCatalog />} />
            {datasource && (
              <>
                <Route path="/metadata" render={() => <Metadata />} />
                <Route path="/schema" render={() => <Schema />} />
                <Route path="/actions" render={() => <Actions />} />
                <Route path="/events" render={() => <Events />} />
                <Route path="/test" render={() => <Test />} />
                <Route path="/export" render={() => <Export />} />
              </>
            )}
            <Redirect to="/" />
          </Switch>
        </div>
      </MemoryRouter>
    </DatasourceContext.Provider>
  )
}
